import { useState, useEffect } from "react";
import { Loader2, ArrowRight, FlaskConical } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api } from "../../lib/api";
import { fmtAmountMinor } from "../../lib/usePlans";
import { billingErrorMessage } from "../BillingPanel";
import i18n from "../../i18n";

const tr = (k, o) => i18n.t(k, o);

const Row = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between gap-4 py-1.5 text-sm ${strong ? "font-semibold text-slate-900 border-t border-slate-200 mt-1 pt-2" : "text-slate-600"}`}>
    <span>{label}</span><span data-testid={testid}>{value}</span>
  </div>
);

// Choix offre -> Récapitulatif -> « Continuer vers le paiement » (facture Dolibarr puis session Mobupay).
export const CheckoutDialog = ({ workspaceId, plan, onClose }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!plan) return;
    setPreview(null); setError(null);
    api.post(`/workspaces/${workspaceId}/billing/checkout/preview`, { plan })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(billingErrorMessage(e.response?.data?.detail)));
  }, [workspaceId, plan]);

  const pay = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/billing/checkout`, { plan });
      window.location.assign(data.redirect_url);
    } catch (e) { setError(billingErrorMessage(e.response?.data?.detail)); setBusy(false); }
  };
  const money = (v) => fmtAmountMinor(v || 0, preview.currency);

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="checkout-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{tr("billing:v1.checkout.title")}</DialogTitle>
          <DialogDescription>{tr("billing:v1.checkout.subtitle")}</DialogDescription>
        </DialogHeader>
        {!preview && !error && <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" />}
        {preview && (
          <div data-testid="checkout-recap">
            <Row label={tr("billing:v1.checkout.plan")} value={preview.plan_name} testid="checkout-plan" />
            <Row label={tr("billing:v1.checkout.members")} value={preview.billed_members} testid="checkout-members" />
            <Row label={tr("billing:v1.checkout.ht")} value={money(preview.amount_ht_minor)} testid="checkout-ht" />
            <Row label={tr("billing:v1.checkout.tax", { rate: preview.tax_rate_percent })} value={money(preview.tax_minor)} testid="checkout-tax" />
            <Row label={tr("billing:v1.checkout.ttc")} value={money(preview.amount_ttc_minor)} testid="checkout-ttc" strong />
            <p className="text-xs text-slate-500 mt-3">{tr("billing:v1.checkout.note")}</p>
            {preview.test_mode && (
              <p data-testid="checkout-test-mode" className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800">
                <FlaskConical className="w-3.5 h-3.5" /> {tr("billing:v1.testMode")}
              </p>
            )}
          </div>
        )}
        {error && <p data-testid="checkout-error" role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-2">
          <Button variant="outline" data-testid="checkout-cancel-btn" disabled={busy} onClick={onClose}>{tr("billing:v1.checkout.cancel")}</Button>
          <Button data-testid="checkout-continue-btn" disabled={!preview || !preview.currency_supported || busy} onClick={pay}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            {tr("billing:v1.checkout.continue")}
          </Button>
        </div>
        {preview && !preview.currency_supported && (
          <p data-testid="checkout-currency-unsupported" className="text-xs text-amber-700">{tr("billing:v1.errors.PAYMENT_CURRENCY_NOT_SUPPORTED")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
